import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, CheckCircle2, MessageCircle, PackageSearch, TicketPercent, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useShop } from "@/context/ShopContext";
import { formatPrice, PAYMENT_METHODS, PROOF_METHODS, PAYMENT_PROOF_NOTICE, waLink } from "@/lib/constants";
import Reveal from "@/components/Reveal";

const EMPTY_FORM = { name: "", phone: "", email: "", address: "", city: "", notes: "" };

const Cart = () => {
  const { cart, updateQty, removeFromCart, cartTotal, clearCart } = useShop();
  const [form, setForm] = useState(EMPTY_FORM);
  const [payment, setPayment] = useState("virement");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [settings, setSettings] = useState({ crypto_address: "", shipping_fee: 0, free_shipping_from: 0 });

  useEffect(() => {
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const discount = promo ? Math.round(cartTotal * promo.percent) / 100 : 0;
  const afterPromo = Math.max(0, cartTotal - discount);
  const shipping = settings.free_shipping_from > 0 && afterPromo >= settings.free_shipping_from
    ? 0
    : Number(settings.shipping_fee) || 0;
  const grandTotal = afterPromo + shipping;

  const applyPromo = async () => {
    setPromoError("");
    if (!promoInput.trim()) return;
    try {
      const res = await api.get("/promos/validate", { params: { code: promoInput } });
      setPromo(res.data);
      toast.success(`Code ${res.data.code} appliqué : −${res.data.percent}%`);
    } catch (err) {
      setPromo(null);
      setPromoError(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/orders", {
        customer: form,
        items: cart.map((i) => ({ perfume_id: i.id, size: i.size, qty: i.qty })),
        payment_method: payment,
        promo_code: promo?.code || null,
      });
      setOrder(res.data);
      clearCart();
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (order) {
    const method = PAYMENT_METHODS.find((m) => m.id === order.payment_method);
    return (
      <div className="mx-auto max-w-2xl px-4 py-16" data-testid="order-success">
        <Reveal className="rounded-3xl border border-[#EADFCB] bg-white p-8 text-center shadow-xl shadow-amber-950/5">
          <CheckCircle2 size={56} className="mx-auto text-[#C59B27]" />
          <h1 className="mt-4 font-serif text-3xl text-[#1A1714]">Merci {order.customer.name.split(" ")[0]} !</h1>
          <p className="mt-2 text-sm text-[#5C5449]">
            Votre commande a bien été reçue et un email de confirmation vient de vous être envoyé. Conservez votre code de suivi :
          </p>
          <p data-testid="order-code" className="mt-4 font-accent text-3xl tracking-[0.2em] text-[#C59B27]">{order.code}</p>

          <div className="mt-6 rounded-2xl bg-[#F7EEDB] p-5 text-left">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">
              Paiement : {method?.label}
            </p>
            <p data-testid="payment-instruction" className="mt-2 break-words text-sm leading-relaxed text-[#5C5449]">
              {order.payment_instruction || method?.instruction}
            </p>
            {order.discount > 0 && (
              <p className="mt-3 text-sm font-semibold text-[#C59B27]">
                Code {order.promo_code} appliqué : −{formatPrice(order.discount)}
              </p>
            )}
            <p className="mt-1 text-sm text-[#5C5449]">
              Livraison : {order.shipping ? formatPrice(order.shipping) : "offerte"}
            </p>
            <p className="mt-3 text-sm font-bold text-[#1A1714]">Total à payer : {formatPrice(order.total)}</p>
          </div>

          {PROOF_METHODS.includes(order.payment_method) && (
            <div data-testid="payment-proof-notice" className="mt-4 rounded-2xl border border-[#C59B27]/40 bg-white p-4 text-left">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#C59B27]">Preuve de paiement requise</p>
              <p className="mt-1 text-sm leading-relaxed text-[#5C5449]">{PAYMENT_PROOF_NOTICE}</p>
            </div>
          )}

          <div className="mt-6 space-y-2 text-left">
            {order.items.map((i) => (
              <div key={`${i.perfume_id}-${i.size}`} className="flex justify-between border-b border-[#EADFCB] pb-2 text-sm">
                <span className="text-[#5C5449]">{i.name} ({i.brand}) — {i.size} ml × {i.qty}</span>
                <span className="font-semibold text-[#1A1714]">{formatPrice(i.unit_price * i.qty)}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/suivi" data-testid="go-tracking-button" className="flex items-center gap-2 rounded-full bg-[#1A1714] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#C59B27] active:scale-95">
              <PackageSearch size={16} /> Suivre ma commande
            </Link>
            <a
              href={waLink(`Bonjour ANM Paris, je viens de passer la commande ${order.code}.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white transition-all hover:scale-105 active:scale-95"
            >
              <MessageCircle size={16} /> Confirmer sur WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Commande</p>
        <h1 className="mt-2 font-serif text-3xl text-[#1A1714] sm:text-4xl">Votre panier</h1>
      </Reveal>

      {cart.length === 0 ? (
        <div className="py-20 text-center" data-testid="empty-cart">
          <p className="font-serif text-2xl text-[#1A1714]">Votre panier est vide</p>
          <Link to="/boutique" className="mt-6 inline-block rounded-full bg-[#C59B27] px-8 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95">
            Découvrir la boutique
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid gap-10 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-3" data-testid="cart-items">
            {cart.map((i) => (
              <Reveal key={`${i.id}-${i.size}`} className="flex gap-4 rounded-2xl border border-[#EADFCB] bg-white p-4">
                <img src={i.image} alt={i.name} className="h-24 w-20 rounded-xl object-cover" />
                <div className="flex flex-1 flex-col">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#C59B27]">{i.brand}</p>
                      <p className="font-serif text-lg font-semibold text-[#1A1714]">{i.name}</p>
                      <p className="text-xs text-[#5C5449]">{i.size} ml — {formatPrice(i.price)}</p>
                    </div>
                    <button data-testid={`remove-item-${i.id}-${i.size}`} onClick={() => removeFromCart(i.id, i.size)} className="text-[#5C5449] transition-colors hover:text-red-500" aria-label="Retirer">
                      <Trash2 size={17} />
                    </button>
                  </div>
                  <div className="mt-auto flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQty(i.id, i.size, i.qty - 1)} className="flex h-8 w-8 items-center justify-center rounded-full border border-[#EADFCB] text-sm hover:border-[#C59B27]">−</button>
                      <span className="w-6 text-center text-sm font-semibold">{i.qty}</span>
                      <button onClick={() => updateQty(i.id, i.size, i.qty + 1)} className="flex h-8 w-8 items-center justify-center rounded-full border border-[#EADFCB] text-sm hover:border-[#C59B27]">+</button>
                    </div>
                    <span className="font-serif text-lg font-bold text-[#1A1714]">{formatPrice(i.price * i.qty)}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1} className="lg:col-span-2">
            <form onSubmit={submit} className="rounded-2xl border border-[#EADFCB] bg-white p-6" data-testid="checkout-form">
              <p className="font-serif text-xl font-semibold text-[#1A1714]">Finaliser ma commande</p>
              <div className="mt-5 space-y-3">
                <input data-testid="checkout-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom complet" className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
                <input data-testid="checkout-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Téléphone (WhatsApp de préférence)" className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
                <input data-testid="checkout-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (confirmation + suivi de commande)" className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
                <input data-testid="checkout-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse de livraison" className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
                <input data-testid="checkout-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville / Pays" className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
                <textarea data-testid="checkout-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Instructions spéciales (optionnel)" className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
              </div>

              <p className="mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">
                <TicketPercent size={14} /> Code promo
              </p>
              <div className="mt-2 flex gap-2">
                <input
                  data-testid="promo-code-input"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                  placeholder="Ex : ANM20"
                  className="flex-1 rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm uppercase outline-none focus:border-[#C59B27]"
                />
                <button type="button" data-testid="promo-apply-button" onClick={applyPromo} className="rounded-xl bg-[#1A1714] px-5 py-2.5 text-xs font-semibold text-white transition-all hover:bg-[#C59B27] active:scale-95">
                  Appliquer
                </button>
              </div>
              {promo && (
                <p data-testid="promo-applied" className="mt-2 text-xs font-semibold text-[#C59B27]">
                  Code {promo.code} appliqué : −{promo.percent}% (−{formatPrice(discount)})
                </p>
              )}
              {promoError && <p data-testid="promo-error" className="mt-2 text-xs font-medium text-red-600">{promoError}</p>}

              <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Mode de paiement</p>
              <div className="mt-3 space-y-2" data-testid="payment-methods">
                {PAYMENT_METHODS.map((m) => (
                  <button
                    type="button"
                    key={m.id}
                    data-testid={`payment-${m.id}`}
                    onClick={() => setPayment(m.id)}
                    className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-all active:scale-[0.98] ${
                      payment === m.id ? "border-[#C59B27] bg-[#F7EEDB]" : "border-[#EADFCB] bg-white hover:border-[#C59B27]/50"
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-[#1A1714]">{m.label}</span>
                      <span className="block break-words text-xs text-[#5C5449]">
                        {m.id === "crypto" && settings.crypto_address ? `USDC (ERC20) : ${settings.crypto_address}` : m.desc}
                      </span>
                    </span>
                    <span className={`ml-3 h-4 w-4 shrink-0 rounded-full border-2 ${payment === m.id ? "border-[#C59B27] bg-[#C59B27]" : "border-[#EADFCB]"}`} />
                  </button>
                ))}
              </div>

              {PROOF_METHODS.includes(payment) && (
                <p className="mt-3 rounded-xl bg-[#F7EEDB] p-3 text-xs leading-relaxed text-[#5C5449]" data-testid="payment-proof-hint">
                  {PAYMENT_PROOF_NOTICE}
                </p>
              )}

              {settings.free_shipping_from > 0 && shipping > 0 && (
                <p className="mt-3 flex items-center gap-2 rounded-xl bg-[#F7EEDB] p-3 text-xs font-medium text-[#5C5449]" data-testid="free-shipping-hint">
                  <Truck size={14} className="shrink-0 text-[#C59B27]" />
                  Plus que {formatPrice(settings.free_shipping_from - afterPromo)} pour la livraison offerte !
                </p>
              )}

              <div className="mt-5 space-y-1.5 border-t border-[#EADFCB] pt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5C5449]">Sous-total</span>
                  <span className={promo ? "text-[#5C5449] line-through" : "text-[#1A1714]"}>{formatPrice(cartTotal)}</span>
                </div>
                {promo && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#C59B27]">Code {promo.code}</span>
                    <span className="font-semibold text-[#C59B27]">−{formatPrice(discount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#5C5449]">Livraison</span>
                  <span data-testid="shipping-cost" className={shipping === 0 ? "font-semibold text-[#C59B27]" : "text-[#1A1714]"}>
                    {shipping === 0 ? "Offerte" : formatPrice(shipping)}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm font-semibold text-[#1A1714]">Total</span>
                  <span data-testid="cart-total" className="font-serif text-2xl font-bold text-[#1A1714]">{formatPrice(grandTotal)}</span>
                </div>
              </div>
              <button
                data-testid="checkout-submit-button"
                disabled={loading}
                className="mt-4 w-full rounded-full bg-[#C59B27] py-4 text-sm font-semibold text-white shadow-lg shadow-[#C59B27]/25 transition-all hover:bg-[#A8821B] active:scale-95 disabled:opacity-50"
              >
                {loading ? "Envoi en cours…" : "Passer ma commande"}
              </button>
            </form>
          </Reveal>
        </div>
      )}
    </div>
  );
};

export default Cart;
